import { Fragment, useMemo, useState, useEffect, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import "./Style.css";
import { FaSave, FaTrash, FaCoffee, FaInfo } from "react-icons/fa";
import { getDefaultCustomPayouts, getDefaultTournamentLevels } from "./utils/tournamentDefaults";

const GAME_TYPE_OPTIONS = ["NLH", "PLO"];

function InitialLoadingScreen() {
  return (
    <div
      className="loading-container"
      style={{ padding: "4rem", textAlign: "center", color: "white" }}
    >
      <h2 style={{ marginBottom: "1.5rem", fontWeight: "700" }}>
        Gathering Data...
      </h2>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <div
          className="spinner"
          style={{
            border: "4px solid rgba(255,255,255,0.1)",
            borderTop: "4px solid #6366f1",
            borderRadius: "50%",
            width: "50px",
            height: "50px",
            animation: "spin 1s linear infinite",
          }}
        ></div>
      </div>
      <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
    </div>
  );
}

export default function TournamentCreate() {
  const { cardroom } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const templateId = queryParams.get("id");
  const token = localStorage.getItem("token");
  const permission = localStorage.getItem("permission");
  const dealerMode = Number(localStorage.getItem("dealer_mode") || 0);

  // State
  const [name, setName] = useState("");
  const [originalName, setOriginalName] = useState("");
  const [currSession, setCurrSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveStatus, setSaveStatus] = useState("idle");
  const [savedSignature, setSavedSignature] = useState(null);
  const [savedTemplateId, setSavedTemplateId] = useState(templateId);
  
  const [creating, setCreating] = useState(false);
  
  // Structure Config
  const [startingChips, setStartingChips] = useState(10000);
  const [bonusChips, setBonusChips] = useState(10000); 
  const [buyinAddonChips, setBuyinAddonChips] = useState(0);
  const [levels, setLevels] = useState(getDefaultTournamentLevels);
  
  // Rebuy & Addon Config
  const [rebuysChips, setRebuysChips] = useState(10000);
  const [rebuysCost, setRebuysCost] = useState(0);
  const [rebuyCutoffLevel, setRebuyCutoffLevel] = useState(6); 
  const [addonChips, setAddonChips] = useState(10000);
  const [rebuysAddOnChips, setRebuysAddOnChips] = useState(10000);

  // Payout Config
  const [payoutMode, setPayoutMode] = useState("percent"); // "percent" | "fixed" | "custom"
  const [payoutValue, setPayoutValue] = useState(15); 
  const [payoutExponent, setPayoutExponent] = useState(1.5);
  const [previewPrizePool, setPreviewPrizePool] = useState(2000);
  const [previewBuyIn, setPreviewBuyIn] = useState(100);
  const [minCashPercentDropdown, setMinCashPercentDropdown] = useState('150');
  const [minCashCustom, setMinCashCustom] = useState(150);
  const [customPayouts, setCustomPayouts] = useState(getDefaultCustomPayouts);
  const [draggingLevelIndex, setDraggingLevelIndex] = useState(null);
  const [dragOriginIndex, setDragOriginIndex] = useState(null);
  const [dragOverLevelIndex, setDragOverLevelIndex] = useState(null);
  const draggingLevelIndexRef = useRef(null);
  const dragOverLevelIndexRef = useRef(null);
  const levelDragCommittedRef = useRef(false);

  const isViewMode = queryParams.get("view") === "true";

const [matchAnteToBB, setMatchAnteToBB] = useState(false);
const [mixedGames, setMixedGames] = useState(false);

  // Auth Effect
  useEffect(() => {
    if (!token) {
      navigate("/");
      return;
    }
    if (dealerMode === 2) {
      navigate(`/${cardroom}/dealerdashboard`);
      return;
    }
    if (!permission?.includes("O") && !permission?.includes("Y")) {
      navigate(`/${cardroom}/game`);
      return;
    }
    axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;

    const loadData = async () => {
         setLoading(true);
         try {
             const { data } = await axios.get(`/${cardroom}/api/cardroom`);
             setCurrSession(data.active);
setCreating((!templateId));
             if (templateId) {
                 setSavedTemplateId(templateId);
                 const { data: tData } = await axios.get(`/${cardroom}/api/tournaments/templates/${templateId}`);
                 setOriginalName(tData.name || "");
                 setName(tData.name || "");
                 const c = tData.config_json || {};
                 if (c.startingChips !== undefined) setStartingChips(c.startingChips);
                 if (c.bonusChips !== undefined) setBonusChips(c.bonusChips);
                 if (c.buyinAddonChips !== undefined) setBuyinAddonChips(c.buyinAddonChips);
                 if (c.rebuysChips !== undefined) setRebuysChips(c.rebuysChips);
                 if (c.rebuysCost !== undefined) setRebuysCost(c.rebuysCost);
                 if (c.rebuyCutoffLevel !== undefined) setRebuyCutoffLevel(c.rebuyCutoffLevel);
                 if (c.addonChips !== undefined) setAddonChips(c.addonChips);
                 if (c.rebuysAddOnChips !== undefined) setRebuysAddOnChips(c.rebuysAddOnChips);
                 if (c.payoutMode !== undefined) setPayoutMode(c.payoutMode);
                 if (c.payoutValue !== undefined) setPayoutValue(c.payoutValue);
                 if (c.payoutExponent !== undefined) setPayoutExponent(c.payoutExponent);
                 if (c.minCashPercent !== undefined) {
                     if (['150', '200', '250', '300'].includes(c.minCashPercent.toString())) {
                         setMinCashPercentDropdown(c.minCashPercent.toString());
                     } else {
                         setMinCashPercentDropdown('custom');
                         setMinCashCustom(c.minCashPercent);
                     }
                 }
                 if (Array.isArray(c.customPayouts) && c.customPayouts.length > 0) {
                     setCustomPayouts(c.customPayouts.map((p, idx) => ({
                         place: Number(p.place || idx + 1),
                         percentage: Number(p.percentage || 0)
                     })));
                 }
                 if (c.levels && Array.isArray(c.levels) && c.levels.length > 0) {
                     setLevels(c.levels);
                 } else {
                     setLevels(getDefaultTournamentLevels());
                 }
                 setMixedGames(Boolean(c.mixedGames || (Array.isArray(c.levels) && c.levels.some(level => level.gameType))));
             }
         } catch(err) {
             console.error(err);
         } finally {
             setLoading(false);
         }
    };
    loadData();

  }, [token, cardroom, navigate, templateId, dealerMode, permission]);

// Enforce BB Ante on all existing levels
  const handleToggleMatchAnte = (checked) => {
    setMatchAnteToBB(checked);
    if (checked) {
      const updatedLevels = levels.map(l => ({
        ...l,
        ante: l.isBreak ? 0 : l.bb
      }));
      setLevels(updatedLevels);
    }
  };

  // Reset all antes to 0
  const clearAllAntes = () => {
    setMatchAnteToBB(false);
    const updatedLevels = levels.map(l => ({ ...l, ante: 0 }));
    setLevels(updatedLevels);
  };

  const minutesMatchFirstRow =
    levels.length <= 1 ||
    levels.every((level) => Number(level.duration) === Number(levels[0]?.duration));

  const matchAllMinutesToFirstRow = () => {
    if (isViewMode || levels.length === 0) return;
    const firstDuration = Number(levels[0]?.duration) || 0;
    setLevels(levels.map((level) => ({ ...level, duration: firstDuration })));
  };

  const gameTypesMatchFirstRow =
    levels.length <= 1 ||
    levels.every((level) => (level.gameType || "") === (levels[0]?.gameType || ""));

  const matchAllGameTypesToFirstRow = () => {
    if (isViewMode || levels.length === 0) return;
    const firstGameType = levels[0]?.gameType || "";
    setLevels(levels.map((level) => ({ ...level, gameType: firstGameType })));
  };

  const alternateGameTypes = () => {
    if (isViewMode || levels.length === 0) return;
    const firstGameType = levels[0]?.gameType || "";
    setLevels(levels.map((level, index) => ({ ...level, gameType: index % 2 === 0 ? firstGameType : (GAME_TYPE_OPTIONS.find(gt => gt !== firstGameType) || "") })));
  };

  const formatNumberWithCommas = (value) => {
    const number = Number(value) || 0;
    return number.toLocaleString();
  };

  const parseCommaNumber = (value) => {
    const parsed = Number(String(value).replace(/,/g, ""));
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const roundBlindToTwoSignificantDigits = (value) => {
    const number = Math.max(0, Math.round(Number(value) || 0));
    if (number === 0) return 0;
    const magnitude = Math.floor(Math.log10(number));
    const factor = Math.pow(10, Math.max(0, magnitude - 1));
    const leadingTwoDigits = number / factor;
    const firstDigit = Math.floor(leadingTwoDigits / 10);
    const secondDigit = leadingTwoDigits - (firstDigit * 10) < 2.5 ? 0 : 5;
    return (firstDigit * 10 + secondDigit) * factor;
  };

const addLevel = () => {
    let lastPlayableLevel = null;
    for (let i = levels.length - 1; i >= 0; i--) {
        if (!levels[i].isBreak) {
            lastPlayableLevel = levels[i];
            break;
        }
    }

    const roundBlind = (value) => {
      if (value < 100) return Math.round(value / 25) * 25;
      return roundBlindToTwoSignificantDigits(value);
    };
    const newSb = lastPlayableLevel ? roundBlind(lastPlayableLevel.sb * 1.5) : 50;
    const newBb = lastPlayableLevel ? roundBlind(lastPlayableLevel.bb * 1.5) : 100;
    const newAnte = matchAnteToBB ? newBb : (lastPlayableLevel ? lastPlayableLevel.ante : 0);
    const duration = lastPlayableLevel ? lastPlayableLevel.duration : 20;

    setLevels([...levels, { 
      sb: newSb, 
      bb: newBb, 
      ante: newAnte, 
      duration: duration, 
      isBreak: false,
      gameType: mixedGames ? (lastPlayableLevel?.gameType || "") : undefined
    }]);
  };

  const addBreak = () => {
      setLevels([...levels, { 
        sb: 0, 
        bb: 0, 
        ante: 0, 
        duration: 10, 
        isBreak: true,
        gameType: mixedGames ? "" : undefined
      }]);
  };

  const removeLevel = (idx) => {
    setLevels(levels.filter((_, i) => i !== idx));
  };

  const updateLevel = (idx, field, val) => {
    const newLevels = [...levels];
    newLevels[idx] = { ...newLevels[idx], [field]: val };
    setLevels(newLevels);
  };

  const getDropInsertIndex = (fromIdx, toIdx) => {
    if (fromIdx === null || toIdx === null || fromIdx === toIdx) return null;
    const insertIdx = fromIdx < toIdx ? toIdx - 1 : toIdx;
    return insertIdx === fromIdx ? null : insertIdx;
  };

  const moveLevelOnDrop = (toIdx) => {
    const fromIdx = draggingLevelIndexRef.current;
    const insertIdx = getDropInsertIndex(fromIdx, toIdx);
    if (isViewMode || insertIdx === null) return;
    setLevels(prevLevels => {
      if (!prevLevels[fromIdx] || (toIdx !== prevLevels.length && !prevLevels[toIdx])) return prevLevels;
      const newLevels = [...prevLevels];
      const [level] = newLevels.splice(fromIdx, 1);
      newLevels.splice(insertIdx, 0, level);
      return newLevels;
    });
  };

  const getLevelDropTargetFromPointer = (event, idx) => {
    if (idx !== levels.length - 1) return idx;
    const rect = event.currentTarget.getBoundingClientRect();
    return event.clientY > rect.top + rect.height / 2 ? levels.length : idx;
  };

  const updateDragOverLevel = (idx) => {
    if (isViewMode || draggingLevelIndexRef.current === null) return;
    dragOverLevelIndexRef.current = idx;
    setDragOverLevelIndex(idx);
  };

  const commitLevelDrag = (fallbackIdx = null) => {
    levelDragCommittedRef.current = true;
    const toIdx = dragOverLevelIndexRef.current ?? fallbackIdx;
    moveLevelOnDrop(toIdx);
  };

  const finishLevelDrag = (fallbackIdx = null) => {
    if (!levelDragCommittedRef.current) {
      commitLevelDrag(fallbackIdx);
    }
    stopLevelDrag();
  };

  const stopLevelDrag = () => {
    draggingLevelIndexRef.current = null;
    dragOverLevelIndexRef.current = null;
    levelDragCommittedRef.current = false;
    setDraggingLevelIndex(null);
    setDragOriginIndex(null);
    setDragOverLevelIndex(null);
  };

  const updateCustomPayout = (idx, percentage) => {
    setCustomPayouts(prev => prev.map((p, i) => (
      i === idx ? { ...p, percentage: Number(percentage) } : p
    )));
  };

  const addCustomPayout = () => {
    setCustomPayouts(prev => [...prev, { place: prev.length + 1, percentage: 0 }]);
  };

  const removeCustomPayout = (idx) => {
    setCustomPayouts(prev => {
      if (prev.length <= 1) return prev;
      return prev.filter((_, i) => i !== idx).map((p, i) => ({ ...p, place: i + 1 }));
    });
  };

  const customPayoutTotal = customPayouts.reduce((sum, p) => sum + (Number(p.percentage) || 0), 0);
  const isCustomPayoutValid = payoutMode !== 'custom' || Math.abs(customPayoutTotal - 100) <= 0.001;

  const templateConfig = useMemo(() => {
    const savedLevels = mixedGames
      ? levels
      : levels.map(({ gameType, ...level }) => level);

    return {
      startingChips,
      bonusChips,
      buyinAddonChips,
      rebuysChips,
      rebuysCost,
      rebuyCutoffLevel,
      addonChips,
      rebuysAddOnChips,
      payoutMode,
      payoutValue,
      payoutExponent,
      minCashPercent: minCashPercentDropdown === 'custom' ? minCashCustom : Number(minCashPercentDropdown),
      customPayouts: customPayouts.map((p, idx) => ({
        place: idx + 1,
        percentage: Number(p.percentage) || 0
      })),
      mixedGames,
      levels: savedLevels
    };
  }, [
    startingChips,
    bonusChips,
    buyinAddonChips,
    rebuysChips,
    rebuysCost,
    rebuyCutoffLevel,
    addonChips,
    rebuysAddOnChips,
    payoutMode,
    payoutValue,
    payoutExponent,
    minCashPercentDropdown,
    minCashCustom,
    customPayouts,
    mixedGames,
    levels
  ]);

  const currentSaveSignature = useMemo(() => JSON.stringify({
    name: name.trim(),
    config: templateConfig
  }), [name, templateConfig]);

  const hasUnsavedChanges = savedSignature !== null && currentSaveSignature !== savedSignature;

  useEffect(() => {
    if (!loading && savedSignature === null) {
      setSavedSignature(currentSaveSignature);
    }
  }, [currentSaveSignature, loading, savedSignature]);

  useEffect(() => {
    if (saveStatus === "saved" && savedSignature && currentSaveSignature !== savedSignature) {
      setSaveStatus("idle");
    }
  }, [currentSaveSignature, saveStatus, savedSignature]);

  const saveButtonText = saving
    ? "Saving..."
    : saveStatus === "saved" && !hasUnsavedChanges
    ? "Saved"
    : creating && !templateId
    ? "Create New Template"
    : hasUnsavedChanges
    ? "Save Template"
    : "No Changes";

  const templateDisplayName = name.trim() || "Untitled";
  const templateSubtitle = !templateId && !savedTemplateId
    ? "Creating a new tournament template."
    : isViewMode
    ? `Viewing ${templateDisplayName} tournament template.`
    : `Editing ${originalName || templateDisplayName} tournament template.`;

  const handleNumberFocus = (e) => {
    if (isViewMode) return;
    if (String(e.target.value) === "0") {
      e.target.value = "";
      return;
    }
    e.target.select();
  };

  const preventNumberInputWheel = (e) => {
    if (e.target?.tagName === "INPUT" && e.target.type === "number") {
      e.preventDefault();
    }
  };
  const preventLeadingZero = (e) => {
  if (e.target.type !== 'number') return;
  const val = e.target.value;
  if (/^0\d/.test(val)) {
    e.target.value = val.replace(/^0+/, '') || '0';
  }
};

  const previewInputStyle = {
    background: 'rgba(250, 204, 21, 0.12)',
    borderColor: 'rgba(250, 204, 21, 0.55)',
    boxShadow: 'inset 0 0 0 1px rgba(250, 204, 21, 0.2)'
  };

  const infoIconStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '16px',
    height: '16px',
    marginLeft: '0.35rem',
    borderRadius: '50%',
    border: '1px solid rgba(255,255,255,0.25)',
    background: 'rgba(255,255,255,0.08)',
    color: 'var(--game-text-secondary)',
    fontSize: '0.6rem',
    verticalAlign: 'middle',
    cursor: 'help'
  };

  const levelInsertPreviewStyle = {
    background: 'rgba(250, 204, 21, 0.18)',
    outline: '1px solid rgba(250, 204, 21, 0.85)',
    boxShadow: '0 0 0 1px rgba(250, 204, 21, 0.25)',
    pointerEvents: 'none'
  };

  const renderLevelInsertPreview = (draggedLevel, key) => {
    if (!draggedLevel) return null;
    return (
      <tr key={key} style={levelInsertPreviewStyle}>
        <td>
          {draggedLevel.isBreak ? <FaCoffee title="Break" /> : levels.slice(0, dragOriginIndex + 1).filter(level => !level.isBreak).length}
          <span style={{fontSize: '0.65em', display:'block', color: 'rgba(250, 204, 21, 0.95)', fontWeight: 700}}>INSERT</span>
        </td>
        {mixedGames && (
          <td style={{textAlign: 'center', opacity: 0.85}}>{draggedLevel.gameType || 'None'}</td>
        )}
        {draggedLevel.isBreak ? (
          <td colSpan="3" style={{textAlign: 'center', fontStyle: 'italic', opacity: 0.85}}>BREAK</td>
        ) : (
          <>
            <td style={{textAlign: 'center'}}>{formatNumberWithCommas(draggedLevel.sb)}</td>
            <td style={{textAlign: 'center'}}>{formatNumberWithCommas(draggedLevel.bb)}</td>
            <td style={{textAlign: 'center'}}>{formatNumberWithCommas(draggedLevel.ante)}</td>
          </>
        )}
        <td style={{textAlign: 'center'}}>{draggedLevel.duration}</td>
        <td></td>
      </tr>
    );
  };

  const calculatePayouts = (prizepool, numPlaces, exponent, buyIn, minCashPercent) => {
      if (numPlaces <= 0 || prizepool <= 0) return [];
      
      const minCash = buyIn * (minCashPercent / 100);
      let totalMinCash = minCash * numPlaces;
      
      let actualMinCash = minCash;
      if (totalMinCash > prizepool) {
          actualMinCash = prizepool / numPlaces; 
          totalMinCash = prizepool;
      }


      // ── Shifted Inverse Power Law (Zipf with shift) ──────────────────────
      // Model: w_i = 1 / (i + c)^k,  normalised so sum(w_i) = 1
      // (k, c) pairs fitted via least-squares from WSOP payout dataset:
      //   exponent 1.0 → Flatter  (k=2.50, c=6.5)
      //   exponent 1.2 → Flat     (k=2.50, c=5.0)
      //   exponent 1.5 → Normal   (k=2.50, c=4.5)
      //   exponent 2.0 → Steep    (k=2.41, c=3.0)
      // Slider values outside these anchors extrapolate the (k,c) linearly.
      const PRESETS = [
          { exp: 1.0, k: 2.50, c: 6.5 },
          { exp: 1.2, k: 2.50, c: 5.0 },
          { exp: 1.5, k: 2.50, c: 4.5 },
          { exp: 2.0, k: 2.41, c: 3.0 },
      ];

      let left = PRESETS[0], right = PRESETS[PRESETS.length - 1];
      for (let i = 0; i < PRESETS.length - 1; i++) {
          if (exponent >= PRESETS[i].exp && exponent <= PRESETS[i + 1].exp) {
              left = PRESETS[i]; right = PRESETS[i + 1]; break;
          }
      }
      const t = right.exp === left.exp ? 0 : (exponent - left.exp) / (right.exp - left.exp);
      const k = left.k + t * (right.k - left.k);
      const c = left.c + t * (right.c - left.c);

      // Compute weights
      const weights = [];
      let totalWeight = 0;
      for (let i = 1; i <= numPlaces; i++) {
          const w = 1 / Math.pow(i + c, k);
          weights.push(w);
          totalWeight += w;
      }
      if (totalWeight <= 0) totalWeight = 1;

      const remainingPool = prizepool - actualMinCash * numPlaces;

      let payouts = weights.map((w, i) => ({
          place: i + 1,
          amount: actualMinCash + (w / totalWeight) * remainingPool
      }));

      // ── Grouping step-function (bracket lower placements) ────────────────
      const tiers = [
          { start: 10, end: 27,       step: 3  },
          { start: 28, end: 99,       step: 9  },
          { start: 100, end: Infinity, step: 10 },
      ];
      for (const tier of tiers) {
          if (numPlaces < tier.start) continue;
          const tierEnd = Math.min(numPlaces, tier.end);
          for (let i = tier.start; i <= tierEnd; i += tier.step) {
              const groupEnd = Math.min(i + tier.step - 1, tierEnd);
              if (groupEnd > i) {
                  let sum = 0;
                  for (let j = i; j <= groupEnd; j++) sum += payouts[j - 1].amount;
                  const avg = sum / (groupEnd - i + 1);
                  for (let j = i; j <= groupEnd; j++) payouts[j - 1].amount = avg;
              }
          }
      }

      // ── Round and fix rounding error on 1st place ────────────────────────
      let totalAssigned = 0;
      for (let i = 0; i < payouts.length; i++) {
          payouts[i].amount = Math.round(payouts[i].amount);
          totalAssigned += payouts[i].amount;
      }
      if (payouts.length > 0) payouts[0].amount += prizepool - totalAssigned;

      for (let i = 0; i < payouts.length; i++) {
          payouts[i].percentage = (payouts[i].amount / prizepool) * 100;
      }

      return payouts;
  };

  const handleSave = async () => {
    if (saving || !hasUnsavedChanges) return;
    if (!name) return alert("Please enter a template name");
    if (!isCustomPayoutValid) return alert("Custom payout percentages must add up to 100%");

    setSaving(true);
    setSaveStatus("saving");

    try {
      const targetTemplateId = savedTemplateId || templateId;

      if (targetTemplateId) {
        await axios.put(`/${cardroom}/api/tournaments/templates/${targetTemplateId}`, {
          name,
          config: templateConfig
        });
      } else {
        const { data } = await axios.post(`/${cardroom}/api/tournaments/templates`, {
          name,
          config: templateConfig
        });
        
        if (data?.id) setSavedTemplateId(String(data.id));
      }
      setSavedSignature(currentSaveSignature);
      setSaveStatus("saved");
    } catch (err) {
      console.error("Save failed", err);
      setSaveStatus("idle");
      alert("Failed to save template");
    } finally {
      setSaving(false);
      setCreating(false);
    }
  };

  if (loading) {
    return (
      <div className="game">
        <div className="game-container">
          <InitialLoadingScreen />
        </div>
      </div>
    );
  }

  return (
    <div className="game" onWheelCapture={preventNumberInputWheel} onInputCapture={preventLeadingZero}>
      <div className="game-container" style={{maxWidth: '1000px', margin: '0 auto'}}>
        <div style={{ marginBottom: '1.5rem' }}>
          <h1 className="game-section__title" style={{ marginBottom: '0.35rem' }}>
            {(creating && !templateId ? "New " : "") + "Tournament Template " + (creating && !templateId ? "Created" : "")}
          </h1>
          <p style={{ margin: 0, color: 'var(--game-text-secondary)', fontSize: '0.95rem' }}>
            {templateSubtitle}
          </p>
        </div>
        
        <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem'}}>
            <div>
              
<div style={{ marginBottom: '2rem', display: 'flex', gap: '1rem' }}>
    {isViewMode ? (
        /* Close button for View Mode */
        <button 
            type="button"
            className="game-btn game-btn--secondary" 
            onClick={() => window.close()}
        >
            Close
        </button>
    ) : (
        /* Back button for Edit/Create Mode */
        <button 
            type="button"
            className="game-btn game-btn--secondary" 
            onClick={() => navigate(`/${cardroom}/tournamentsHome`)}
        >
            Back
        </button>
    )}

    {/* Save Button (only if not in view mode) */}
    {!isViewMode && (
        <button className="game-btn game-btn--primary" onClick={handleSave} disabled={!isCustomPayoutValid || saving || !hasUnsavedChanges}>
            <FaSave /> {saveButtonText}
        </button>
    )}
</div>

                <div className="game-card" style={{padding: '1.5rem', background: 'var(--game-bg-secondary)', marginBottom: '1.5rem'}}>
                    <label className="game-label">Template Name</label>
                    <input 
                    className="game-input" 
                    value={name} 
                    disabled={isViewMode}
                    onChange={e => setName(e.target.value)}
                    placeholder="Detailed Structure Name"
                    />
                </div>

                <div className="game-card" style={{padding: '1.5rem', background: 'var(--game-bg-secondary)', marginBottom: '1.5rem'}}>
                    <h3 className="game-section__title" style={{marginTop: 0, fontSize: '1rem'}}>Buy-in Chip Counts</h3>
                    <div style={{display: 'flex', gap: '1rem', marginTop: '1rem'}}>
                        <div style={{flex: 1}}>
                            <label className="game-label">
                                Base Starting Stack
                                <span
                                    style={infoIconStyle}
                                    title="The mandatory chip stack a player receives for only paying the standard initial entry fee."
                                    aria-label="Base starting stack info"
                                >
                                    <FaInfo />
                                </span>
                            </label>
                            <input type="number" className="game-input" disabled={isViewMode} value={startingChips} onWheel={(e) => e.target.blur()} onFocus={handleNumberFocus} onChange={e => setStartingChips(Number(e.target.value))} />
                        </div>
                        <div style={{flex: 1}}>
                            <label className="game-label">
                                Bonus Chips
                                <span
                                    style={infoIconStyle}
                                    title="Optional extra chips that players can receive during their initial entry for promotional incentives."
                                    aria-label="Bonus chips info"
                                >
                                    <FaInfo />
                                </span>
                            </label>
                            <input type="number" className="game-input" disabled={isViewMode} onWheel={(e) => e.target.blur()} value={bonusChips} onFocus={handleNumberFocus} onChange={e => setBonusChips(Number(e.target.value))} />
                        </div>
                    </div>
                    <div style={{marginTop: '1rem'}}>
                        <label className="game-label">
                            Dealer Add-On Chips
                            <span
                                style={infoIconStyle}
                                title="An optional chip purchase available to players at initial registration."
                                aria-label="Dealer add-on chips info"
                            >
                                <FaInfo />
                            </span>
                        </label>
                        <input type="number" className="game-input" disabled={isViewMode} onWheel={(e) => e.target.blur()} value={buyinAddonChips} onFocus={handleNumberFocus} onChange={e => setBuyinAddonChips(Number(e.target.value))} />
                    </div>
                </div>

                <div className="game-card" style={{padding: '1.5rem', background: 'var(--game-bg-secondary)', marginBottom: '1.5rem'}}>
                    <h3 className="game-section__title" style={{marginTop: 0, fontSize: '1rem'}}>Rebuys & Tournament Add-On</h3>
                    <div style={{display: 'flex', gap: '1rem'}}>
                         <div style={{flex: 1}}>
                            <label className="game-label">
                                Base Rebuy Stack
                                <span
                                    style={infoIconStyle}
                                    title="The baseline chip stack a player receives for a standard rebuy without dealer add-on."
                                    aria-label="Base rebuy stack info"
                                >
                                    <FaInfo />
                                </span>
                            </label>
                            <input type="number" className="game-input" disabled={isViewMode} onWheel={(e) => e.target.blur()}  value={rebuysChips} onFocus={handleNumberFocus} onChange={e => setRebuysChips(Number(e.target.value))} />
                        </div>
                        <div style={{flex: 1}}>
                            <label className="game-label">
                                Rebuy Cutoff Level
                                <span
                                    style={infoIconStyle}
                                    title="The final blind level during which players are permitted to rebuy."
                                    aria-label="Rebuy cutoff level info"
                                >
                                    <FaInfo />
                                </span>
                            </label>
                            <input type="number" className="game-input" disabled={isViewMode} onWheel={(e) => e.target.blur()} value={rebuyCutoffLevel} onFocus={handleNumberFocus} onChange={e => setRebuyCutoffLevel(Number(e.target.value))} />
                        </div>
                     </div>
                     <div style={{display: 'flex', gap: '1rem', marginTop: '1rem'}}>
                        <div style={{flex: 1}}>
                            <label className="game-label">
                                Rebuy Dealer Add-On Chips
                                <span
                                    style={infoIconStyle}
                                    title="An optional chip purchase available to players during a rebuy."
                                    aria-label="Rebuy dealer add-on chips info"
                                >
                                    <FaInfo />
                                </span>
                            </label>
                            <input type="number" className="game-input" disabled={isViewMode} onWheel={(e) => e.target.blur()}  value={rebuysAddOnChips} onFocus={handleNumberFocus} onChange={e => setRebuysAddOnChips(Number(e.target.value))} />
                        </div>
                         <div style={{flex: 1}}>
                            <label className="game-label">
                                Add-On Chips
                                <span
                                    style={infoIconStyle}
                                    title="Usually an optional, one-time chip purchase available to all remaining players at the end of the rebuy period."
                                    aria-label="Add-on chips info"
                                >
                                    <FaInfo />
                                </span>
                            </label>
                            <input type="number" className="game-input" disabled={isViewMode} onWheel={(e) => e.target.blur()} value={addonChips} onFocus={handleNumberFocus} onChange={e => setAddonChips(Number(e.target.value))} />
                        </div>
                    </div>
                </div>
                
                <div className="game-card" style={{padding: '1.5rem', background: 'var(--game-bg-secondary)', marginBottom: '1.5rem'}}>
                    <h3 className="game-section__title" style={{marginTop: 0, fontSize: '1rem'}}>Payout Structure</h3>
                    
                    <div style={{display: 'flex', gap: '1rem', marginBottom: '1rem'}}>
                        <button 
                            className={`game-btn ${payoutMode === 'percent' ? 'game-btn--primary' : 'game-btn--secondary'}`}
                            disabled={isViewMode}
                            onClick={() => setPayoutMode('percent')}
                            style={{flex: 1}}
                        >
                            % of Field
                        </button>
                        <button 
                            className={`game-btn ${payoutMode === 'fixed' ? 'game-btn--primary' : 'game-btn--secondary'}`}
                            disabled={isViewMode}
                            onClick={() => setPayoutMode('fixed')}
                            style={{flex: 1}}
                        >
                            Fixed Places
                        </button>
                        <button 
                            className={`game-btn ${payoutMode === 'custom' ? 'game-btn--primary' : 'game-btn--secondary'}`}
                            disabled={isViewMode}
                            onClick={() => setPayoutMode('custom')}
                            style={{flex: 1}}
                        >
                            Custom
                        </button>
                    </div>

                    {payoutMode !== 'custom' ? (
                    <>
                    <div style={{marginBottom: '1rem'}}>
                        <label className="game-label">
                            {payoutMode === 'percent' ? 'Percentage Paid' : 'Number of Places Paid'}
                        </label>
                        <input 
                            type="number" 
                            className="game-input" 
                            value={payoutValue} 
                            onWheel={(e) => e.target.blur()}
                            disabled={isViewMode}
                            onFocus={handleNumberFocus}
                            onChange={e => setPayoutValue(Number(e.target.value))} 
                            placeholder={payoutMode === 'percent' ? "e.g. 15" : "e.g. 3"}
                        />
                    </div>

                    {(() => {
                        const steepnessOptions = [
                            { label: 'Flatter', value: 1.0 },
                            { label: 'Flat', value: 1.2 },
                            { label: 'Normal', value: 1.5 },
                            { label: 'Steep', value: 2.0 },
                            { label: 'Extremely Steep', value: 2.5 }
                        ];
                        const matchedSteepness = steepnessOptions.find(o => o.value === payoutExponent)?.label || 'Custom';
                        
                        return (
                            <div style={{display: 'flex', gap: '1rem', marginBottom: '1rem'}}>
                                <div style={{flex: 1}}>
                                    <label className="game-label">Min % of Buy-In Cash</label>
                                    <div style={{display: 'flex', gap: '0.5rem'}}>
                                        <select 
                                            className="game-input" 
                                            value={minCashPercentDropdown} 
                                            onChange={e => setMinCashPercentDropdown(e.target.value)}
                                            disabled={isViewMode}
                                        >
                                            <option value="150">150%</option>
                                            <option value="200">200%</option>
                                            <option value="250">250%</option>
                                            <option value="300">300%</option>
                                            <option value="custom">Custom</option>
                                        </select>
                                        {minCashPercentDropdown === 'custom' && (
                                            <input 
                                                type="number" 
                                                className="game-input" 
                                                value={minCashCustom === 0 ? '' : minCashCustom} 
                                                onFocus={handleNumberFocus}
                                                onWheel={(e) => e.target.blur()}
                                                onChange={e => setMinCashCustom(e.target.value === '' ? 0 : Number(e.target.value))}
                                                disabled={isViewMode}
                                                style={{width: '80px'}}
                                            />
                                        )}
                                    </div>
                                </div>
                                <div style={{flex: 1}}>
                                    <label className="game-label">Steepness Type</label>
                                    <select 
                                        className="game-input"
                                        value={matchedSteepness}
                                        onChange={e => {
                                            if (e.target.value !== 'Custom') {
                                                const opt = steepnessOptions.find(o => o.label === e.target.value);
                                                if (opt) setPayoutExponent(opt.value);
                                            }
                                        }}
                                        disabled={isViewMode}
                                    >
                                        <option value="Flatter">Flatter (1.0)</option>
                                        <option value="Flat">Flat (1.2)</option>
                                        <option value="Normal">Normal (1.5)</option>
                                        <option value="Steep">Steep (2.0)</option>
                                        <option value="Extremely Steep">Extremely Steep (2.5)</option>
                                        <option value="Custom" disabled>Custom</option>
                                    </select>
                                </div>
                            </div>
                        );
                    })()}

                    <div style={{marginBottom: '1rem'}}>
                        <label className="game-label">Payout Exponent Slider: {payoutExponent.toFixed(2)}</label>
                        <input 
                            type="range" 
                            min="1" 
                            max="3" 
                            step="0.05" 
                            value={payoutExponent} 
                            disabled={isViewMode}
                            onChange={e => setPayoutExponent(Number(e.target.value))}
                            style={{width: '100%', accentColor: 'var(--game-primary)'}}
                        />
                        <div style={{display: 'flex', justifyContent: 'space-between', fontSize: '0.8rem', opacity: 0.7}}>
                            <span>Flatter (1.0)</span>
                            <span>Steep (2.0)</span>
                            <span>Super Steep (3.0)</span>
                        </div>
                    </div>

                    <div style={{display: 'flex', gap: '1rem', marginBottom: '1rem'}}>
                        <div style={{flex: 1}}>
                            <label className="game-label">Preview Example Buy-In ($)</label>
                            <input 
                                type="number" 
                                className="game-input" 
                                style={previewInputStyle}
                                value={previewBuyIn === 0 ? '' : previewBuyIn} 
                                onChange={e => setPreviewBuyIn(e.target.value === '' ? 0 : Number(e.target.value))} 
                                onFocus={handleNumberFocus}
                                onWheel={(e) => e.target.blur()}
                            />
                        </div>
                        <div style={{flex: 1}}>
                            <label className="game-label">Preview Total Prize Pool ($)</label>
                            <input 
                                type="number" 
                                className="game-input" 
                                style={previewInputStyle}
                                value={previewPrizePool === 0 ? '' : previewPrizePool} 
                                onChange={e => {
                                    const val = e.target.value;
                                    setPreviewPrizePool(val === '' ? 0 : Number(val));
                                }} 
                                onFocus={handleNumberFocus}
                                onWheel={(e) => e.target.blur()}
                            />
                        </div>
                    </div>

  <div style={{ marginTop: '1.5rem' }}>
    <h4 className="game-label" style={{ marginBottom: '0.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '0.3rem' }}>
        Payout Distribution (Advanced)
    </h4>
    <div className="game-table-container" style={{ width: '100%' }}>
        <table className="game-table" style={{ 
            fontSize: '0.85rem', 
            width: '100%',           // Ensure it takes the full container width
            tableLayout: 'fixed',    // Forces columns to respect assigned widths
            borderCollapse: 'separate', 
            borderSpacing: '0 5px'   // Adds vertical breathing room between rows
        }}>
            <thead>
                <tr>
                    {/* Assigned percentages to stretch the columns evenly or as desired */}
                    <th style={{ width: '40%', padding: '12px 8px', textAlign: 'center' }}>Place</th>
                    <th style={{ width: '30%', padding: '12px 8px', textAlign: 'center' }}>%</th>
                    <th style={{ width: '30%', padding: '12px 8px', textAlign: 'center' }}>Est Amount</th>
                </tr>
            </thead>
            <tbody style={{ borderTop: '1px solid rgba(255,255,255,0.1)' }}>
                {(() => {
                    // ... your existing logic remains exactly the same ...
                    const derivedPlayers = Math.max(1, Math.floor(previewPrizePool / Math.max(1, previewBuyIn)));
                    let pp = 0;
                    if (payoutMode === 'fixed') {
                        pp = payoutValue;
                    } else {
                        pp = Math.floor(derivedPlayers * (payoutValue / 100));
                    }
                    if (pp < 1 && derivedPlayers > 0) pp = 1;
                    
                    const totalPrizePool = previewPrizePool;
                    const usedMinCashPercent = minCashPercentDropdown === 'custom' ? minCashCustom : Number(minCashPercentDropdown);
                    
                    const previewPayouts = calculatePayouts(totalPrizePool, pp, payoutExponent, previewBuyIn, usedMinCashPercent);
                    
                    let condensed = [];
                    for(let i=0; i<previewPayouts.length; i++) {
                        let p = previewPayouts[i];
                        let startPlace = p.place;
                        let endPlace = p.place;
                        let groupAmount = p.amount;
                        while(i + 1 < previewPayouts.length && previewPayouts[i+1].amount === groupAmount) {
                            endPlace = previewPayouts[i+1].place;
                            i++;
                        }
                        if (startPlace === endPlace) {
                            condensed.push({ placeStr: `${startPlace}`, ...p });
                        } else {
                            condensed.push({ placeStr: `${startPlace}-${endPlace}`, ...p });
                        }
                    }

                    return condensed.map((p, idx) => (
                        <tr key={idx} style={{ background: 'rgba(255,255,255,0.02)' }}>
                            {/* Increased padding here makes the table feel much larger/stretched */}
                            <td style={{ padding: '12px 8px', textAlign: 'center' }}>{p.placeStr}</td>
                            <td style={{ padding: '12px 8px', textAlign: 'center' }}>{p.percentage.toFixed(1)}%</td>
                            <td style={{ padding: '12px 8px', textAlign: 'center', fontWeight: 'bold' }}>${p.amount.toLocaleString()}</td>
                        </tr>
                    ));
                })()}
            </tbody>
        </table>
    </div>
</div>
                    </>
                    ) : (
                    <div style={{ marginTop: '1.5rem' }}>
                        <div style={{marginBottom: '1rem'}}>
                            <label className="game-label">Preview Total Prize Pool ($)</label>
                            <input 
                                type="number" 
                                className="game-input" 
                                style={previewInputStyle}
                                value={previewPrizePool === 0 ? '' : previewPrizePool} 
                                onChange={e => setPreviewPrizePool(e.target.value === '' ? 0 : Number(e.target.value))}
                                onFocus={handleNumberFocus}
                                onWheel={(e) => e.target.blur()}
                            />
                        </div>

                        <h4 className="game-label" style={{ marginBottom: '0.5rem', borderBottom: '1px solid rgba(255,255,255,0.1)', paddingBottom: '0.3rem' }}>
                            Payouts
                        </h4>
                        {Math.abs(customPayoutTotal - 100) > 0.001 && (
                            <div style={{ color: 'var(--game-danger)', fontSize: '0.85rem', marginBottom: '0.75rem', fontWeight: 600 }}>
                                Payout percentages total {customPayoutTotal.toFixed(1)}%. They should add up to 100%.
                            </div>
                        )}
                        <div className="game-table-container" style={{ width: '100%', overflowX: 'hidden' }}>
                            <table className="game-table" style={{ fontSize: '0.85rem', width: '100%', tableLayout: 'fixed', borderCollapse: 'separate', borderSpacing: '0 5px' }}>
                                <thead>
                                    <tr>
                                        <th style={{ width: '24%', padding: '12px 6px', textAlign: 'center' }}>Place</th>
                                        <th style={{ width: '28%', padding: '12px 6px', textAlign: 'center' }}>%</th>
                                        <th style={{ width: '34%', padding: '12px 6px', textAlign: 'center' }}>Est Amount</th>
                                        <th style={{ width: '14%', padding: '12px 6px', textAlign: 'center' }}></th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {customPayouts.map((p, idx) => {
                                        const pct = Number(p.percentage) || 0;
                                        const amount = Math.round((previewPrizePool || 0) * (pct / 100));
                                        return (
                                            <tr key={idx} style={{ background: 'rgba(255,255,255,0.02)' }}>
                                                <td style={{ padding: '12px 6px', textAlign: 'center' }}>{idx + 1}</td>
                                                <td style={{ padding: '12px 6px', textAlign: 'center' }}>
                                                    <input
                                                        type="number"
                                                        className="game-input"
                                                        disabled={isViewMode}
                                                        value={p.percentage}
                                                        onFocus={handleNumberFocus}
                                                        onChange={e => updateCustomPayout(idx, e.target.value)}
                                                        onWheel={(e) => e.target.blur()}
                                                        style={{ width: '100%', maxWidth: '96px', minWidth: 0, boxSizing: 'border-box', margin: '0 auto', textAlign: 'center', padding: '0.55rem 0.4rem' }}
                                                    />
                                                </td>
                                                <td style={{ padding: '12px 6px', textAlign: 'center', fontWeight: 'bold', whiteSpace: 'nowrap' }}>${amount.toLocaleString()}</td>
                                                <td style={{ padding: '12px 6px', textAlign: 'center' }}>
                                                    <button className="game-notification__close" disabled={isViewMode || customPayouts.length <= 1} onClick={() => removeCustomPayout(idx)}><FaTrash /></button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                        {!isViewMode && (
                            <button className="game-btn game-btn--secondary" style={{ width: '100%', marginTop: '1rem' }} onClick={addCustomPayout}>
                                + Add Place Paid
                            </button>
                        )}
                    </div>
                    )}
                </div>
            </div>

            <div>
                <h2 className="game-section__title">Blind Structure</h2>
                <div className="tournament-structure-actions" style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
      {/* Match Ante to BB Toggle */}
      <label className="game-filter-chip" style={{ marginBottom: '1rem', cursor: isViewMode ? 'default' : 'pointer', opacity: isViewMode ? 0.6 : 1 }}>
        <input 
          type="checkbox" 
          disabled={isViewMode}
          checked={matchAnteToBB}
          onChange={(e) => handleToggleMatchAnte(e.target.checked)}
        />
        <span>Match Ante to BB</span>
      </label>

      <label className="game-filter-chip" style={{ marginBottom: '1rem', cursor: isViewMode ? 'default' : 'pointer', opacity: isViewMode ? 0.6 : 1 }}>
        <input
          type="checkbox"
          disabled={isViewMode}
          checked={mixedGames}
          onChange={(e) => setMixedGames(e.target.checked)}
        />
        <span>Mixed Games</span>
      </label>


            {!isViewMode && mixedGames && !gameTypesMatchFirstRow && (
        <button
          type="button"
          className="game-btn game-btn--sm game-btn--secondary"
          onClick={matchAllGameTypesToFirstRow}
          style={{ marginBottom: '1rem', padding: '0.4rem 0.8rem', fontSize: '0.75rem' }}
        >
          Match All Game Types
        </button>
      )}
            {!isViewMode && mixedGames && (
        <button
          type="button"
          className="game-btn game-btn--sm game-btn--secondary"
          onClick={alternateGameTypes}
          style={{ marginBottom: '1rem', padding: '0.4rem 0.8rem', fontSize: '0.75rem' }}
        >
          Alternate Game Types
        </button>
      )}
      {!isViewMode && !minutesMatchFirstRow && (
        <button
          type="button"
          className="game-btn game-btn--sm game-btn--secondary"
          onClick={matchAllMinutesToFirstRow}
          style={{ marginBottom: '1rem', padding: '0.4rem 0.8rem', fontSize: '0.75rem' }}
        >
          Match All Mins
        </button>
      )}

            {/* Clear All Antes (Only shows if any level has an ante > 0) */}
      {!isViewMode && levels.some(l => l.ante > 0) && (
        <button 
          type="button" 
          className="game-btn game-btn--sm game-btn--secondary" 
          onClick={clearAllAntes}
          style={{ marginBottom: '1rem', padding: '0.4rem 0.8rem', fontSize: '0.75rem' }}
        >
          Clear All Antes
        </button>
      )}
    </div>
                <div className="game-table-container tournament-structure-table-wrap">
                    <table className="game-table tournament-structure-table">
                        <thead>
                            <tr>
                                <th>Lvl</th>
                                {mixedGames && <th>Game Type</th>}
                                <th>SB</th>
                                <th>BB</th>
                                <th>Ante</th>
                                <th>Mins</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody
                            onDragOver={e => {
                                if (isViewMode || draggingLevelIndexRef.current === null) return;
                                e.preventDefault();
                                e.dataTransfer.dropEffect = 'move';
                            }}
                            onDrop={e => {
                                if (isViewMode || draggingLevelIndexRef.current === null) return;
                                e.preventDefault();
                                finishLevelDrag();
                            }}
                        >
                            {levels.map((l, i) => {
                                const levelNum = levels.slice(0, i + 1).filter(level => !level.isBreak).length;
                                const isRebuyCutoff = !l.isBreak && levelNum === rebuyCutoffLevel;
                                const isDragging = draggingLevelIndex === i;
                                const isDragOrigin = dragOriginIndex === i;
                                const dropInsertIndex = getDropInsertIndex(dragOriginIndex, dragOverLevelIndex);
                                const draggedLevel = dropInsertIndex !== null ? levels[dragOriginIndex] : null;
                                const showInsertPreview = draggedLevel && dragOverLevelIndex === i;
                                const rowStyle = {
                                    background: isDragOrigin
                                        ? 'rgba(250, 204, 21, 0.14)'
                                        : l.isBreak 
                                        ? 'rgba(255,255,255,0.05)' 
                                        : (isRebuyCutoff ? 'rgba(239, 68, 68, 0.15)' : 'transparent'),
                                    borderLeft: isRebuyCutoff ? '3px solid var(--game-danger)' : 'none',
                                    cursor: !isViewMode ? (isDragging ? 'grabbing' : 'grab') : 'default',
                                    outline: isDragOrigin ? '1px dashed rgba(250, 204, 21, 0.85)' : 'none',
                                    boxShadow: isDragOrigin ? 'inset 0 0 0 1px rgba(250, 204, 21, 0.25)' : 'none',
                                    opacity: 1,
                                    transition: 'background 120ms ease, box-shadow 120ms ease, opacity 120ms ease'
                                };
                                return (
                                <Fragment key={i}>
                                {showInsertPreview && renderLevelInsertPreview(draggedLevel, `insert-preview-${i}`)}
                                <tr
                                    key={i}
                                    style={rowStyle}
                                    draggable={!isViewMode}
                                    onDragStart={e => {
                                        if (isViewMode) return;
                                        draggingLevelIndexRef.current = i;
                                        dragOverLevelIndexRef.current = null;
                                        levelDragCommittedRef.current = false;
                                        setDraggingLevelIndex(i);
                                        setDragOriginIndex(i);
                                        setDragOverLevelIndex(null);
                                        e.dataTransfer.effectAllowed = 'move';
                                        e.dataTransfer.setData('text/plain', String(i));
                                    }}
                                    onDragEnter={e => updateDragOverLevel(getLevelDropTargetFromPointer(e, i))}
                                    onDragOver={e => {
                                        if (isViewMode) return;
                                        e.preventDefault();
                                        e.dataTransfer.dropEffect = 'move';
                                        updateDragOverLevel(getLevelDropTargetFromPointer(e, i));
                                    }}
                                    onDrop={e => {
                                        if (isViewMode) return;
                                        e.preventDefault();
                                        finishLevelDrag(getLevelDropTargetFromPointer(e, i));
                                    }}
                                    onDragEnd={() => finishLevelDrag(i)}
                                >
                                    <td>
                                        {l.isBreak ? <FaCoffee title="Break" /> : levelNum}
                                        {isDragOrigin && <span style={{fontSize: '0.65em', display:'block', color: 'rgba(250, 204, 21, 0.95)', fontWeight: 700}}>DRAGGING</span>}
                                        {isRebuyCutoff && <span style={{fontSize: '0.7em', display:'block', color: 'var(--game-danger)'}}>CUTOFF</span>}
                                    </td>
                                    {mixedGames && (
                                        <td>
                                            <select
                                                className="game-input"
                                                disabled={isViewMode}
                                                value={l.gameType || ""}
                                                onChange={e => updateLevel(i, 'gameType', e.target.value)}
                                                style={{ width: '100%', minWidth: 0 }}
                                            >
                                                <option value=""></option>
                                                {GAME_TYPE_OPTIONS.map(gameType => (
                                                    <option key={gameType} value={gameType}>{gameType}</option>
                                                ))}
                                            </select>
                                        </td>
                                    )}
                                    {l.isBreak ? (
                                        <td colSpan="3" style={{textAlign: 'center', fontStyle: 'italic', opacity: 0.7}}>
                                            BREAK
                                        </td>
                                    ) : (
                                        <>
                                            <td>
                                                <input type="text" inputMode="numeric" disabled={isViewMode} style={{width: '72px'}} className="game-input" value={formatNumberWithCommas(l.sb)} onFocus={handleNumberFocus} onChange={e => updateLevel(i, 'sb', parseCommaNumber(e.target.value))} onWheel={(e) => e.target.blur()} />
                                            </td>
                                            <td>
                                                <input type="text" inputMode="numeric" disabled={isViewMode} style={{width: '72px'}} className="game-input" value={formatNumberWithCommas(l.bb)} onFocus={handleNumberFocus} onChange={e => updateLevel(i, 'bb', parseCommaNumber(e.target.value))} onWheel={(e) => e.target.blur()} />
                                            </td>
                                            <td>
                                                <input type="text" inputMode="numeric" disabled={isViewMode} style={{width: '72px'}} className="game-input" value={formatNumberWithCommas(l.ante)} onFocus={handleNumberFocus} onChange={e => updateLevel(i, 'ante', parseCommaNumber(e.target.value))} onWheel={(e) => e.target.blur()} />
                                            </td>
                                        </>
                                    )}
                                    <td>
                                        <input type="number" disabled={isViewMode} style={{width: '50px'}} className="game-input" value={l.duration} onFocus={handleNumberFocus} onChange={e => updateLevel(i, 'duration', Number(e.target.value))} onWheel={(e) => e.target.blur()} />
                                    </td>
                                    <td>
                                        <button className="game-notification__close" disabled={isViewMode} onClick={() => removeLevel(i)}><FaTrash /></button>
                                    </td>
                                </tr>
                                </Fragment>
                            )})}
                            {dragOverLevelIndex === levels.length &&
                              renderLevelInsertPreview(
                                getDropInsertIndex(dragOriginIndex, dragOverLevelIndex) !== null
                                  ? levels[dragOriginIndex]
                                  : null,
                                'insert-preview-bottom'
                              )}
                        </tbody>
                    </table>
                    {!isViewMode && (
                                          <div style={{display: 'flex', gap: '0.5rem', marginTop: '1rem'}}>
                         <button className="game-btn game-btn--secondary" disabled={isViewMode} onClick={addLevel} style={{flex: 1}}>
                            + Add Level
                        </button>
                        <button className="game-btn game-btn--secondary" disabled={isViewMode} onClick={addBreak} style={{flex: 1}}>
                            + Add Break
                        </button>
                    </div>
                    )}
                </div>


            </div>
        </div>
      </div>
    </div>
  );
}
